/**
 * Orcaloops primitives — THE row, key caps, dots, chips, pills, buttons, tabs
 * and the property rows every member screen is built from (the seam of the UI
 * redesign; `orcaloops.css` styles them).
 *
 * Presentational only: each primitive is a pure function of its props, holds
 * no state and reads no atom, so a screen composes them and owns the data. The
 * one rule that matters most is that EVERY list in the product is {@link Row}:
 * the nav, the loops, the inbox queue, the layers, the timeline, the history
 * and the Jump palette all share its 32px grid of glyph, name and meta.
 *
 * No `useState`, no `useEffect`.
 *
 * @since 1.0.0
 */
import type { ReactNode } from "react";

/** A printed key cap. @since 1.0.0 */
export const Kbd = ({ children }: { readonly children: ReactNode }): ReactNode => <kbd className="ol-k">{children}
</kbd>;

/**
 * The states a status dot can show: done, waiting on a person, failed, the
 * accent (selected / draft), live (running now), off (not yet, muted) and
 * hollow (not yet, outlined).
 *
 * @since 1.0.0
 */
export type DotState = "ok" | "wait" | "bad" | "acc" | "live" | "off" | "hollow";

/** The status glyph. A `label` makes it an image for assistive tech. @since 1.0.0 */
export const Dot = ({ label, state }: { readonly label?: string; readonly state: DotState }): ReactNode => (
  <span
    aria-label={label}
    className={state === "off" ? "ol-dot" : `ol-dot ol-${state}`}
    data-state={state}
    role={label === undefined ? undefined : "img"}
  />
);

/** A 9px identity tile — the one place a loop's own color shows. @since 1.0.0 */
export const Tile = ({ color }: { readonly color: string }): ReactNode => (
  <span aria-hidden="true" className="ol-tile" style={{ background: color }} />
);

/** A mono chip for an identifier or a default. @since 1.0.0 */
export const Chip = ({ children }: { readonly children: ReactNode }): ReactNode => (
  <span className="ol-chip">{children}</span>
);

/** The tones a pill can carry — each one means a state, never a decoration. @since 1.0.0 */
export type PillTone = "ok" | "wait" | "bad" | "acc" | "off";

/** A state pill; `dot` prefixes it with the tone's dot, `title` explains it on hover. @since 1.0.0 */
export const Pill = (
  { children, dot = false, testId, title, tone }: {
    readonly children: ReactNode;
    readonly dot?: boolean;
    readonly testId?: string;
    readonly title?: string;
    readonly tone: PillTone;
  },
): ReactNode => (
  <span className={`ol-pill ol-${tone}`} data-testid={testId} title={title}>
    {dot ? <span className="ol-dot" /> : null}
    {children}
  </span>
);

/** @since 1.0.0 */
export type BtnVariant = "ghost" | "line" | "fill";

/** @since 1.0.0 */
export interface BtnProps {
  readonly ariaLabel?: string;
  readonly children?: ReactNode;
  readonly danger?: boolean;
  /** Disables the button (never the anchor form). */
  readonly disabled?: boolean;
  /** Renders an anchor instead of a button. */
  readonly href?: string;
  /** The key printed beside the action. */
  readonly kbd?: string;
  readonly onClick?: () => void;
  readonly testId?: string;
  /** `submit` for the one button that submits a form. */
  readonly type?: "button" | "submit";
  readonly variant?: BtnVariant;
}

const classes = (...parts: ReadonlyArray<string | null>): string => parts.filter((part) => part !== null).join(" ");

/** A button: ghost by default, `line` for a bordered one, `fill` for the primary. @since 1.0.0 */
export const Btn = (
  {
    ariaLabel,
    children,
    danger = false,
    disabled = false,
    href,
    kbd,
    onClick,
    testId,
    type = "button",
    variant = "ghost",
  }: BtnProps,
): ReactNode => {
  const className = classes("ol-btn", variant === "ghost" ? null : `ol-${variant}`, danger ? "ol-bad" : null);
  const body = (
    <>
      {children}
      {kbd === undefined ? null : <Kbd>{kbd}</Kbd>}
    </>
  );
  return href === undefined
    ? (
      <button
        aria-label={ariaLabel}
        className={className}
        data-testid={testId}
        disabled={disabled}
        onClick={onClick}
        type={type}
      >
        {body}
      </button>
    )
    : (
      <a aria-label={ariaLabel} className={className} data-testid={testId} href={href} onClick={onClick}>
        {body}
      </a>
    );
};

/** @since 1.0.0 */
export interface RowProps {
  /** With `active`, draws the accent rule down the row's left edge. */
  readonly accent?: boolean;
  readonly active?: boolean;
  /** The muted text after the name, on the same line; it truncates before the name does. A string doubles as its hover title. */
  readonly description?: ReactNode;
  /** The 16px glyph slot: a {@link Dot}, a {@link Tile} or an icon. */
  readonly glyph?: ReactNode;
  /** Renders an anchor. */
  readonly href?: string;
  /** The right-aligned slot: counts, chips, a {@link RowWhen}. */
  readonly meta?: ReactNode;
  /** A string doubles as its hover title. */
  readonly name: ReactNode;
  /** Renders a button (when there is no `href`). */
  readonly onClick?: () => void;
  readonly testId?: string;
}

/**
 * THE row. An anchor with `href`, a button with `onClick`, a plain row with
 * neither — always the same 32px grid of glyph, name and meta.
 *
 * @since 1.0.0
 */
export const Row = (
  { accent = false, active = false, description, glyph, href, meta, name, onClick, testId }: RowProps,
): ReactNode => {
  const className = classes("ol-row", active ? "ol-on" : null, active && accent ? "ol-acc" : null);
  const body = (
    <>
      <span className="ol-g">{glyph}</span>
      <span className="ol-n">
        <span className="ol-nm" title={typeof name === "string" ? name : undefined}>{name}</span>
        {description === undefined ?
          null :
          (
            <span className="ol-ds" title={typeof description === "string" ? description : undefined}>
              {description}
            </span>
          )}
      </span>
      <span className="ol-m">{meta}</span>
    </>
  );
  if (href !== undefined) {
    return (
      <a
        aria-current={active ? "true" : undefined}
        className={className}
        data-testid={testId}
        href={href}
        onClick={onClick}
      >
        {body}
      </a>
    );
  }
  if (onClick !== undefined) {
    return (
      <button aria-pressed={active} className={className} data-testid={testId} onClick={onClick} type="button">
        {body}
      </button>
    );
  }
  return <div className={className} data-testid={testId}>{body}</div>;
};

/** The right-aligned, faint time in a row's meta slot. @since 1.0.0 */
export const RowWhen = ({ children }: { readonly children: ReactNode }): ReactNode => (
  <span className="ol-when">{children}</span>
);

/** A count in a row's meta slot; `hot` colors it with the accent. @since 1.0.0 */
export const RowCount = (
  { children, hot = false }: { readonly children: ReactNode; readonly hot?: boolean },
): ReactNode => <span className={hot ? "ol-cnt ol-hot" : "ol-cnt"}>{children}</span>;

/** A titled group of rows: the 32px header, then the rows. @since 1.0.0 */
export const RowGroup = (
  { children, hint, title, trailing }: {
    readonly children: ReactNode;
    readonly hint?: ReactNode;
    readonly title: ReactNode;
    readonly trailing?: ReactNode;
  },
): ReactNode => (
  <>
    <div className="ol-hd">
      <span>{title}</span>
      {hint === undefined ? null : <span className="ol-t4">{hint}</span>}
      <div className="ol-grow" />
      {trailing}
    </div>
    <div className="ol-rows">{children}</div>
  </>
);

/** A property row in the right column: label left, value right, same 32px rhythm. @since 1.0.0 */
export const Kv = (
  { children, label, wrap = false }: {
    readonly children: ReactNode;
    readonly label: ReactNode;
    readonly wrap?: boolean;
  },
): ReactNode => (
  <div className="ol-kv">
    <span className="ol-k2">{label}</span>
    <span className={wrap ? "ol-v ol-wrap" : "ol-v"}>{children}</span>
  </div>
);

/** A section of the right column: a hairline, the title, then its rows. @since 1.0.0 */
export const PanelSection = (
  { children, title, trailing }: {
    readonly children: ReactNode;
    readonly title: ReactNode;
    readonly trailing?: ReactNode;
  },
): ReactNode => (
  <>
    <div className="ol-rsec">
      <span>{title}</span>
      <div className="ol-grow" />
      {trailing}
    </div>
    {children}
  </>
);

/** @since 1.0.0 */
export interface TabSpec {
  readonly count?: number;
  /** Renders the tab as an anchor that navigates, for tabs that are routes. */
  readonly href?: string;
  readonly id: string;
  readonly label: string;
}

/**
 * A tab strip. `strip` is the 36px underline strip over a list or a canvas;
 * `panel` is the small pill strip at the top of the right column.
 *
 * @since 1.0.0
 */
export const Tabs = (
  { active, kind, onSelect, tabs, trailing }: {
    readonly active: string | null;
    readonly kind: "strip" | "panel";
    readonly onSelect: (id: string) => void;
    readonly tabs: ReadonlyArray<TabSpec>;
    readonly trailing?: ReactNode;
  },
): ReactNode => (
  <div className={kind === "strip" ? "ol-strip" : "ol-rtabs"} role="tablist">
    {tabs.map((tab) => {
      const className = classes(kind === "strip" ? "ol-tab" : null, tab.id === active ? "ol-on" : null);
      const body = (
        <>
          {tab.label}
          {tab.count === undefined ? null : <span className="ol-cnt">{tab.count}</span>}
        </>
      );
      return tab.href === undefined
        ? (
          <button
            aria-selected={tab.id === active}
            className={className}
            data-testid={`ol-tab-${tab.id}`}
            key={tab.id}
            onClick={() => onSelect(tab.id)}
            role="tab"
            type="button"
          >
            {body}
          </button>
        )
        : (
          <a
            aria-selected={tab.id === active}
            className={className}
            data-testid={`ol-tab-${tab.id}`}
            href={tab.href}
            key={tab.id}
            onClick={() => onSelect(tab.id)}
            role="tab"
          >
            {body}
          </a>
        );
    })}
    <div className="ol-grow" />
    {trailing}
  </div>
);
